import { LanguageContainer, useLanguageCookie } from "@axon-enterprise/i18n-helpers";
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import { MixpanelProvider } from './common/tracking/MixpanelProvider';
import AboutAppInfo from './components/AboutAppInfo';
import ErrorBoundary from './components/ErrorBoundary';
import LanguageSelectorDialog from './components/language-selector/LanguageSelectorDialog';
import { useLanguageLoader } from "./i18n";
import { AppRoutes } from './routes';
import { SparkThemeProvider } from "./theme/SparkThemeProvider";
import { getAppConfig } from './utils/config';
import { hasLanguageCookie } from "./utils/cookieHelpers";
import { getEnvConfig } from './utils/envValidation';
import { logger } from "./utils/logger";
import { getSupportPhoneForLocale } from "./utils/phoneFormatter";

const appLogger = logger.withComponent("App");

/**
 * Main application component
 * LanguageContainer manages i18n translations and provides language state
 * SparkThemeProvider wraps AppProvider which provides locale context to all Spark components
 * useLanguageCookie handles language persistence via cookies
 */
function App() {
  const [currentLanguage, setLanguage] = useLanguageCookie();
  const loadLanguage = useLanguageLoader();
  const config = getAppConfig();

  // Check if language cookie exists on mount - if not, show language selector (only when i18n enabled)
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  useEffect(() => {
    if (config.enableI18n && !hasLanguageCookie()) {
      setShowLanguageSelector(true);
    }
  }, [config.enableI18n]);

  // Get locale-aware support phone number
  const supportPhoneDisplay = useMemo(
    () => getSupportPhoneForLocale(currentLanguage),
    [currentLanguage]
  );

  useEffect(() => {
    try {
      getEnvConfig();
    } catch (error) {
      appLogger.error('Environment validation failed:', error);
    }
  }, []);

  const handleLanguageSelectorClose = () => {
    setShowLanguageSelector(false);
  };

  return (
    <ErrorBoundary supportPhoneDisplay={supportPhoneDisplay}>
      <LanguageContainer 
        language={currentLanguage} 
        loadLanguage={loadLanguage}
        onLanguageChange={setLanguage}
      >
        <SparkThemeProvider>
          {config.enableI18n && (
            <LanguageSelectorDialog 
              open={showLanguageSelector} 
              onClose={handleLanguageSelectorClose}
            />
          )}
          <AboutAppInfo />
          <Router>
            <MixpanelProvider>
              <AppRoutes />
            </MixpanelProvider>
          </Router>
        </SparkThemeProvider>
      </LanguageContainer>
    </ErrorBoundary>
  );
}

export default App;